// Shared workspace components: one status system, one item anatomy for actions, activity,
// feedback and review items, and one detail drawer. Styles live in styles/cvi-workspace.css.
import { useEffect, useId, useRef } from "react";
import UiIcon from "../components/UiIcon";
import { statusLabel, workspaceCopy } from "./workspaceCopy";

export const MANAGED = { "data-cvi-i18n": "managed" };

// Every status the workspace shows maps onto a small set of tones, so the same word always
// looks the same wherever it appears.
export const STATUS_TONE = {
    needs_review: "attention", pending_review: "attention", submitted: "attention", resubmitted: "attention", pending: "attention",
    my_action: "primary", in_progress: "info", info: "info", open: "info", new: "info",
    waiting: "neutral", draft: "neutral", not_started: "neutral", locked: "neutral", closed_without_data: "neutral",
    due_soon: "warning", revision_requested: "warning",
    completed: "success", approved: "success", resolved: "success", acknowledged: "success",
    rejected: "danger", overdue: "danger",
};

export function StatusBadge({ status, label, tone, language, className = "" }) {
    const key = String(status || "").toLowerCase();
    const resolved = tone || STATUS_TONE[key] || "neutral";
    return <span className={`cvi-ws-status is-${resolved} ${className}`.trim()}>
        <i aria-hidden="true" />{label ?? statusLabel(key, language)}
    </span>;
}

// Only elevated priority is shown; "normal" stays silent so the list does not read as an alert board.
export function PriorityBadge({ priority, language }) {
    const key = String(priority || "").toLowerCase();
    if (!["high", "urgent"].includes(key)) return null;
    return <span className="cvi-ws-priority is-high">
        <UiIcon name="flag" size={11} strokeWidth={2.2} />{workspaceCopy(language).priority.high}
    </span>;
}

export function initialsOf(name) {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "—";
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export function Avatar({ name, src, size = 32 }) {
    return <span className="cvi-ws-avatar" style={{ "--ws-avatar": `${size}px` }} aria-hidden="true">
        {src ? <img src={src} alt="" /> : initialsOf(name)}
    </span>;
}

export function SectionHeader({ eyebrow, title, subtitle, actions, level = 1, id }) {
    const Heading = level === 1 ? "h1" : "h2";
    return <header className={`cvi-ws-header is-level-${level}`}>
        <div className="cvi-ws-header-copy">
            {eyebrow && <span className="cvi-ws-eyebrow">{eyebrow}</span>}
            <Heading id={id}>{title}</Heading>
            {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="cvi-ws-header-actions">{actions}</div>}
    </header>;
}

// Counter tiles that double as the status filter. Each tile shows how many items it would list.
export function SummaryCounters({ items, value, onChange, label }) {
    return <div className="cvi-ws-counters" role="group" aria-label={label}>
        {items.map(item => {
            const active = value === item.id;
            return <button key={item.id} type="button" className={`cvi-ws-counter is-${item.tone || "neutral"}${active ? " is-active" : ""}${item.zero ? " is-zero" : ""}`}
                aria-pressed={active} onClick={() => onChange?.(active ? "all" : item.id)}>
                <span className="cvi-ws-counter-label">{item.label}</span>
                <strong>{item.count}</strong>
                {item.help && <small>{item.help}</small>}
            </button>;
        })}
    </div>;
}

export function FilterTabs({ options, value, onChange, label, size }) {
    return <div className={`cvi-ws-segmented${size === "sm" ? " is-sm" : ""}`} role="tablist" aria-label={label}>
        {options.map(option => <button key={option.id} type="button" role="tab" aria-selected={value === option.id}
            className={value === option.id ? "is-active" : ""} onClick={() => onChange(option.id)}>
            {option.label}{option.count != null && <span className="cvi-ws-segmented-count">{option.count}</span>}
        </button>)}
    </div>;
}

export function WsEmpty({ icon = "check", title, description, action, tone = "calm" }) {
    return <div className={`cvi-ws-empty is-${tone}`} role="status">
        <span className="cvi-ws-empty-icon" aria-hidden="true"><UiIcon name={icon} size={16} /></span>
        <div>
            <strong>{title}</strong>
            {description && <small>{description}</small>}
        </div>
        {action}
    </div>;
}

// Placeholder rows with the same height as real rows so content does not jump when it arrives.
export function SkeletonRows({ count = 3, variant = "row", label = "Loading…" }) {
    return <div className={`cvi-ws-skeleton is-${variant}`} aria-busy="true" role="status">
        <span className="cvi-xp-sr">{label}</span>
        {Array.from({ length: count }, (_, index) => <div key={index} className="cvi-ws-skeleton-item">
            <i className="is-icon" /><span><i className="is-line" /><i className="is-line is-short" /></span><i className="is-pill" />
        </div>)}
    </div>;
}

export function GroupLabel({ children, count }) {
    return <div className="cvi-ws-group"><span>{children}</span>{count != null && <span className="cvi-ws-group-count">{count}</span>}</div>;
}

export function MetaList({ items }) {
    const shown = items.filter(item => item && item.value != null && item.value !== "");
    if (!shown.length) return null;
    return <dl className="cvi-ws-meta">
        {shown.map(item => <div key={item.label}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}
    </dl>;
}

// ── Activity feed (historical events) ──────────────────────────────────────

export function ActivityFeed({ children, label }) {
    return <ol className="cvi-ws-feed" aria-label={label}>{children}</ol>;
}

export function ActivityItem({ icon = "activity", tone = "neutral", actor, action, entity, context, time, note, change }) {
    return <li className={`cvi-ws-feed-item is-${tone}`}>
        <span className="cvi-ws-feed-marker" aria-hidden="true"><UiIcon name={icon} size={13} /></span>
        <div className="cvi-ws-feed-body">
            <p className="cvi-ws-feed-line">
                {actor && <strong>{actor}</strong>}{actor && action ? " " : ""}{action}{entity ? <> <span className="cvi-ws-feed-entity">{entity}</span></> : null}
            </p>
            {(context || time) && <p className="cvi-ws-feed-meta">
                {context}{context && time ? <span aria-hidden="true"> · </span> : null}{time && <time>{time}</time>}
            </p>}
            {change && <p className="cvi-ws-feed-change">{change}</p>}
            {note && <blockquote className="cvi-ws-feed-note">{note}</blockquote>}
        </div>
    </li>;
}

// ── Action item (something the user still has to do) ──────────────────────

export function ActionItem({ icon = "activity", title, context, detail, due, dueTone, status, statusLabel: label, priority, language, cta, onCta, ctaVariant = "primary" }) {
    return <article className={`cvi-ws-action${status ? ` is-${status}` : ""}`}>
        <span className="cvi-ws-action-icon" aria-hidden="true"><UiIcon name={icon} size={15} /></span>
        <div className="cvi-ws-action-main">
            <div className="cvi-ws-action-title">
                <h3>{title}</h3>
                <PriorityBadge priority={priority} language={language} />
            </div>
            {context && <p className="cvi-ws-action-context">{context}</p>}
            {detail && <p className="cvi-ws-action-detail">{detail}</p>}
            {(due || label) && <div className="cvi-ws-action-meta">
                {label && <StatusBadge status={status} label={label} language={language} />}
                {due && <span className={`cvi-ws-due${dueTone ? ` is-${dueTone}` : ""}`}><UiIcon name="schedule" size={12} />{due}</span>}
            </div>}
        </div>
        {cta && <button type="button" className={`cvi-ws-btn ${ctaVariant === "primary" ? "is-primary" : "is-secondary"} is-sm`} onClick={onCta}>{cta}</button>}
    </article>;
}

// ── Feedback item ──────────────────────────────────────────────────────────

export function FeedbackItem({ author, authorRole, avatar, avatarName, context, date, status, statusTone, statusText, category, entity, children, actions, language, compact = false }) {
    return <article className={`cvi-ws-feedback${compact ? " is-compact" : ""}`}>
        <header className="cvi-ws-feedback-head">
            {avatar !== false && <Avatar name={avatarName || author} src={avatar} size={compact ? 28 : 32} />}
            <div className="cvi-ws-feedback-who">
                <span className="cvi-ws-feedback-author">{author}{authorRole && <small> · {authorRole}</small>}</span>
                {(context || date) && <span className="cvi-ws-feedback-context">{[context, date].filter(Boolean).join(" · ")}</span>}
            </div>
            {status && <StatusBadge status={status} tone={statusTone} label={statusText} language={language} />}
        </header>
        {children && <div className="cvi-ws-feedback-body">{children}</div>}
        {(category || entity || actions) && <footer className="cvi-ws-feedback-foot">
            <div className="cvi-ws-feedback-tags">
                {category && <span className="cvi-ws-tag">{category}</span>}
                {entity && <span className="cvi-ws-tag is-entity">{entity}</span>}
            </div>
            {actions && <div className="cvi-ws-feedback-actions">{actions}</div>}
        </footer>}
    </article>;
}

// ── Review item (one row in the review inbox) ──────────────────────────────

export function ReviewItem({ icon, avatar, typeLabel, title, person, context, summary, date, status, statusText, flag, cta, ctaPrimary, onOpen, language }) {
    // Rows without an action (e.g. feedback already sent) render as static content, not a button.
    const Hit = onOpen ? "button" : "div";
    const hitProps = onOpen ? { type: "button", onClick: onOpen, "aria-label": `${typeLabel}: ${title}${person ? ` — ${person}` : ""}` } : {};
    return <li className={`cvi-ws-review is-${status}`}>
        <Hit className={`cvi-ws-review-hit${onOpen ? "" : " is-static"}`} {...hitProps}>
            {avatar ? <Avatar name={avatar} size={36} /> : <span className="cvi-ws-review-icon" aria-hidden="true"><UiIcon name={icon} size={16} /></span>}
            <span className="cvi-ws-review-main">
                <span className="cvi-ws-review-type">{typeLabel}{flag && <span className="cvi-ws-review-flag">{flag}</span>}</span>
                <strong className="cvi-ws-review-title">{title}</strong>
                <span className="cvi-ws-review-person">{[person, context].filter(Boolean).join(" · ")}</span>
            </span>
            <span className="cvi-ws-review-mid">
                {summary && <span className="cvi-ws-review-summary">{summary}</span>}
                {date && <span className="cvi-ws-review-date">{date}</span>}
            </span>
            <span className="cvi-ws-review-end">
                <StatusBadge status={status} label={statusText} language={language} />
                {cta && <span className={`cvi-ws-btn is-sm ${ctaPrimary ? "is-secondary" : "is-ghost"}`} aria-hidden="true">{cta}</span>}
            </span>
        </Hit>
    </li>;
}

// ── Detail drawer ──────────────────────────────────────────────────────────

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function DetailDrawer({ open, onClose, typeLabel, typeIcon, title, status, statusText, language, children, footer, width = "md", headerExtra }) {
    const panelRef = useRef(null);
    const titleId = useId();
    const lastFocus = useRef(null);
    // Callers pass inline handlers; keep the latest in a ref so a parent re-render (e.g. a background
    // refresh) does not re-run the open effect and pull focus back to the panel.
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    useEffect(() => {
        if (!open) return undefined;
        lastFocus.current = typeof document !== "undefined" ? document.activeElement : null;
        const panel = panelRef.current;
        panel?.focus({ preventScroll: true });
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = event => {
            if (event.key === "Escape") { event.stopPropagation(); closeRef.current?.(); return; }
            if (event.key !== "Tab" || !panel) return;
            const nodes = [...panel.querySelectorAll(FOCUSABLE)].filter(node => node.offsetParent !== null || node === document.activeElement);
            if (!nodes.length) return;
            const first = nodes[0], last = nodes[nodes.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = previousOverflow;
            lastFocus.current?.focus?.({ preventScroll: true });
        };
    }, [open]);
    if (!open) return null;
    const close = workspaceCopy(language).common.close;
    return <div className="cvi-ws-drawer-root" {...MANAGED}>
        <div className="cvi-ws-drawer-scrim" onClick={onClose} aria-hidden="true" />
        <aside ref={panelRef} className={`cvi-ws-drawer is-${width}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
            <header className="cvi-ws-drawer-head">
                <div className="cvi-ws-drawer-heading">
                    {typeLabel && <span className="cvi-ws-drawer-type">{typeIcon && <UiIcon name={typeIcon} size={13} />}{typeLabel}</span>}
                    <h2 id={titleId}>{title}</h2>
                    {(status || headerExtra) && <div className="cvi-ws-drawer-status">
                        {status && <StatusBadge status={status} label={statusText} language={language} />}
                        {headerExtra}
                    </div>}
                </div>
                <button type="button" className="cvi-ws-icon-btn" onClick={onClose} aria-label={close} title={close}><UiIcon name="close" size={16} /></button>
            </header>
            <div className="cvi-ws-drawer-body">{children}</div>
            {footer && <footer className="cvi-ws-drawer-foot">{footer}</footer>}
        </aside>
    </div>;
}

export function DrawerSection({ title, children, aside }) {
    return <section className="cvi-ws-drawer-section">
        {(title || aside) && <div className="cvi-ws-drawer-section-head">{title && <h3>{title}</h3>}{aside}</div>}
        {children}
    </section>;
}

// ── Record history (status trail of one contribution or indicator value) ───

const HISTORY_ICON = {
    submitted: "activity", resubmitted: "activity", revised: "edit", approved: "check", rejected: "close", revision_requested: "flag",
    created: "plus", updated: "edit", actual_saved: "edit", target_set: "target", attachment_added: "paperclip", attachment_removed: "paperclip",
    file_uploaded: "paperclip", file_removed: "paperclip", locked: "shield",
};
const HISTORY_TONE = { approved: "success", rejected: "danger", revision_requested: "warning", submitted: "info", resubmitted: "info" };
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
const hasPersian = value => /[؀-ۿ]/.test(String(value || ""));

export function RecordHistory({ events = [], language, formatTime, order = "asc" }) {
    const t = workspaceCopy(language);
    if (!events.length) return <p className="cvi-ws-muted">{t.common.noHistory}</p>;
    const rows = order === "desc" ? [...events].reverse() : events;
    return <ActivityFeed label={t.common.history}>
        {rows.map((event, index) => {
            const type = String(event.eventType || event.type || "").toLowerCase();
            const label = t.timeline[type] || statusLabel(event.toStatus || type, language) || t.timeline.fallback;
            // Legacy fallback events carry a Persian role word instead of a name; show no actor then.
            const rawActor = event.actorName || event.by;
            const actor = rawActor === "System" ? t.common.system : rawActor && !hasPersian(rawActor) ? rawActor : null;
            // Only real timestamps are shown; legacy calendar strings are not reliable in EN/FR.
            const stampValue = event.createdAt || (ISO_DATE.test(String(event.date || "")) ? event.date : null);
            const when = stampValue ? formatTime?.(stampValue) : null;
            const change = event.fromStatus && event.toStatus && event.fromStatus !== event.toStatus
                ? t.timeline.statusChange(statusLabel(event.fromStatus, language), statusLabel(event.toStatus, language)) : null;
            return <ActivityItem key={event.id || `${type}-${index}`} icon={HISTORY_ICON[type] || "activity"} tone={HISTORY_TONE[type] || "neutral"}
                actor={label} context={actor ? t.timeline.by(actor) : null} time={when} change={change} note={event.note || null} />;
        })}
    </ActivityFeed>;
}
